import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

const EMPTY_FORM = {
  employee_id: '', from_department_id: '', to_department_id: '',
  from_location: '', to_location: '', effective_date: '',
  reason: '', status: 'pending',
};

function Transfers() {
  const [transfers, setTransfers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, em, dp] = await Promise.allSettled([
        hrms.getTransfers(filterStatus ? { status: filterStatus } : {}),
        hrms.getEmployees({ limit: 500 }),
        hrms.getDepartments(),
      ]);
      if (tr.status === 'fulfilled') setTransfers(tr.value.data || []);
      if (em.status === 'fulfilled') { const d = em.value.data; setEmployees(Array.isArray(d) ? d : d.items || []); }
      if (dp.status === 'fulfilled') setDepartments(dp.value.data || []);
    } catch { } finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditTarget(null); setForm(EMPTY_FORM); setModal(true); };
  const openEdit = (t) => {
    setEditTarget(t);
    setForm({
      employee_id: String(t.employee_id || ''),
      from_department_id: String(t.from_department_id || ''),
      to_department_id: String(t.to_department_id || ''),
      from_location: t.from_location || '',
      to_location: t.to_location || '',
      effective_date: t.effective_date || '',
      reason: t.reason || '',
      status: t.status || 'pending',
    });
    setModal(true);
  };

  const closeModal = () => { setModal(false); setEditTarget(null); setForm(EMPTY_FORM); };

  const handleSave = async () => {
    if (!form.employee_id || !form.effective_date) { alert('Employee and Effective Date are required.'); return; }
    setSaving(true);
    try {
      const payload = {
        employee_id: Number(form.employee_id),
        from_department_id: form.from_department_id ? Number(form.from_department_id) : null,
        to_department_id: form.to_department_id ? Number(form.to_department_id) : null,
        from_location: form.from_location || null,
        to_location: form.to_location || null,
        effective_date: form.effective_date,
        reason: form.reason || null,
        status: form.status,
      };
      if (editTarget) {
        await hrms.updateTransfer(editTarget.id, payload);
      } else {
        await hrms.createTransfer(payload);
      }
      closeModal();
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transfer record?')) return;
    try { await hrms.deleteTransfer(id); load(); } catch { alert('Failed to delete.'); }
  };

  const handleApprove = async (id) => {
    try {
      const rec = transfers.find((t) => t.id === id);
      await hrms.updateTransfer(id, { ...rec, employee_id: rec.employee_id, effective_date: rec.effective_date, status: 'approved' });
      load();
    } catch { alert('Failed to approve.'); }
  };

  const cols = [
    { header: 'Employee', key: 'employee_name', render: (v) => v || '—' },
    { header: 'From Dept', key: 'from_department_name', render: (v) => v || '—' },
    { header: 'To Dept', key: 'to_department_name', render: (v) => v || '—' },
    { header: 'From Location', key: 'from_location', render: (v) => v || '—' },
    { header: 'To Location', key: 'to_location', render: (v) => v || '—' },
    { header: 'Effective Date', key: 'effective_date', render: (v) => v ? new Date(v + 'T12:00:00').toLocaleDateString() : '—' },
    { header: 'Status', key: 'status', render: (v) => <Badge status={v} /> },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
          {row.status === 'pending' && (
            <Button size="sm" variant="success" onClick={() => handleApprove(id)}>Approve</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => openEdit(row)}>Edit</Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(id)}>Delete</Button>
        </div>
      ),
    },
  ];

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box', background: theme.white };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Transfers</h1>
        <Button variant="primary" onClick={openCreate}>+ New Transfer</Button>
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {['', 'pending', 'approved', 'cancelled'].map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            style={{ padding: '0.4rem 1rem', borderRadius: '9999px', border: `1.5px solid ${filterStatus === s ? theme.primary : theme.border}`, background: filterStatus === s ? theme.primary : 'transparent', color: filterStatus === s ? theme.white : theme.textMuted, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <Card padding="0">
        <Table columns={cols} data={transfers} loading={loading} emptyMessage="No transfers found" />
      </Card>

      <Modal isOpen={modal} onClose={closeModal} title={editTarget ? 'Edit Transfer' : 'New Transfer'}
        footer={<><Button variant="outlineGray" onClick={closeModal}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleSave}>Save</Button></>}>

        <label style={lbl}>Employee *</label>
        <select style={inp} value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
          <option value="">Select employee</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} ({e.emp_code})</option>)}
        </select>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={lbl}>Effective Date *</label>
            <input style={inp} type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
          </div>
          <div>
            <label style={lbl}>Status</label>
            <select style={inp} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label style={lbl}>From Department</label>
            <select style={inp} value={form.from_department_id} onChange={(e) => setForm({ ...form, from_department_id: e.target.value })}>
              <option value="">— None —</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>To Department</label>
            <select style={inp} value={form.to_department_id} onChange={(e) => setForm({ ...form, to_department_id: e.target.value })}>
              <option value="">— None —</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>From Location</label>
            <input style={inp} value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} placeholder="e.g. Chennai" />
          </div>
          <div>
            <label style={lbl}>To Location</label>
            <input style={inp} value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} placeholder="e.g. Bangalore" />
          </div>
        </div>

        <label style={lbl}>Reason</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px' }} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason for transfer..." />
      </Modal>
    </div>
  );
}

export default Transfers;
